// Single source of truth for what the signed-in user may see.
//
// /api/user-info/ returns an `access` block resolved server-side from the user's
// party (see core.models.Access). Everything in the app reads it from here rather
// than re-deriving rules or re-fetching the endpoint.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";

// Deny-all. Used before the fetch resolves and if it fails, so a failed lookup can
// never accidentally widen access.
export const DENIED_ACCESS = {
  is_internal: false,
  party: null,
  sections: [],
  visible_parties: [],
  focal_party: null,
  lock_focal: true,
  show_consideration_metrics: false,
  show_logo: false,
};

export const AccessContext = createContext({
  user: null,
  access: DENIED_ACCESS,
  loading: true,
  error: null,
  refresh: () => Promise.resolve(),
});

export function AccessProvider({ children }) {
  const [state, setState] = useState({
    user: null,
    access: DENIED_ACCESS,
    loading: true,
    error: null,
  });

  // Guards against a response landing after unmount.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Re-read the access block. Exposed on the context because signing in happens
  // *after* this provider has already mounted and fetched: the first call goes
  // out while the user is still anonymous and 403s, and a client-side navigate
  // into the dashboard never remounts the provider. Without a re-fetch the shell
  // keeps serving that denied-and-errored result and reports "No dashboard
  // access" until the page is reloaded by hand. Never rejects — a failed lookup
  // resolves to the denied state, so callers can always navigate afterwards.
  const refresh = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true }));
    return axios
      .get("/api/user-info/")
      .then((res) => ({
        user: res.data,
        access: res.data?.access || DENIED_ACCESS,
        loading: false,
        error: null,
      }))
      // Not signed in, or the endpoint failed. Stay denied and let the shell say so.
      .catch((error) => ({ user: null, access: DENIED_ACCESS, loading: false, error }))
      .then((next) => {
        if (mounted.current) setState(next);
        return next;
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

/** Hook form, for function components. Class components use `static contextType`. */
export const useAccess = () => useContext(AccessContext);

/** May this user open this nav section? Keys come from core/sections.py. */
export const canSeeSection = (access, key) =>
  Boolean(access?.sections?.includes(key));

/** May this user see this party's figures in a party-selectable control? */
export const canSeeParty = (access, slug) =>
  Boolean(access?.visible_parties?.includes(slug));

/**
 * HOC form, for class components that need access while building initial state —
 * props are available in the constructor, context is not. Injects `access`,
 * `accessLoading` and `currentUser`.
 */
export function withAccess(Wrapped) {
  const WithAccess = (props) => {
    const { access, loading, user } = useAccess();
    return <Wrapped {...props} access={access} accessLoading={loading} currentUser={user} />;
  };
  WithAccess.displayName = `withAccess(${Wrapped.displayName || Wrapped.name || "Component"})`;
  return WithAccess;
}
